import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CalendarDays, PlaneTakeoff, PlaneLanding, CheckCircle2, RefreshCw, BedDouble } from 'lucide-react';
import API from '../../api/axiosInstance';
import { utcToLADateKey, formatLADateKeyReadable } from '../../utils/laDateUtils';
import { assignGroupsToPeriods, computeCoverage } from '../../utils/bookingCoverage';
import ParticipantBookingPanel from './ParticipantBookingPanel';
import RoomSegmentCard from './RoomSegmentCard';
import '../css/ParticipantBookingTab.css';

/** LA-anchored, matching every other date shown on this tab (panel, confirmed-booking line). */
const formatDate = (val) => {
  if (!val) return '—';
  const key = utcToLADateKey(val);
  return key ? formatLADateKeyReadable(key) : '—';
};

const nightsBetween = (from, to) => {
  const a = new Date(from);
  const b = new Date(to);
  if (isNaN(a) || isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86400000));
};

const coverageBadge = (coverage) => {
  if (coverage.coveredNights === 0) {
    return { label: 'Not booked', className: 'pbt-badge-empty' };
  }
  if (coverage.isFull) {
    return { label: 'Fully booked', className: 'pbt-booked-badge' };
  }
  return {
    label: `Partially booked (${coverage.coveredNights}/${coverage.totalNights} nights)`,
    className: 'pbt-badge-partial',
  };
};

const StayPeriodCard = ({ period, index, assignedGroups, competitionId, participantId, guests, onChanged }) => {
  const [panelOpen, setPanelOpen] = useState(false);

  const stayInKey = utcToLADateKey(period.check_in_date);
  const stayOutKey = utcToLADateKey(period.check_out_date);
  const nights = nightsBetween(period.check_in_date, period.check_out_date);
  const coverage = computeCoverage(period, assignedGroups);
  const badge = coverageBadge(coverage);

  const handleDone = () => {
    setPanelOpen(false);
    onChanged();
  };

  return (
    <div className={`pbt-stay-card${assignedGroups.length > 0 ? ' pbt-stay-card--booked' : ''}`}>
      <div className="pbt-stay-card__row">
        <div className="pbt-stay-card__info">
          <span className="pbt-stay-card__index">Stay {index + 1}</span>
          <span className="pbt-stay-card__dates">
            <span className="pbt-date-chip pbt-date-chip--in">
              <CalendarDays size={13} /> {formatDate(period.check_in_date)}
            </span>
            <span className="pbt-arrow">→</span>
            <span className="pbt-date-chip pbt-date-chip--out">
              <CalendarDays size={13} /> {formatDate(period.check_out_date)}
            </span>
          </span>
          <span className="pbt-nights-badge">{nights} night{nights !== 1 ? 's' : ''}</span>
        </div>

        <div className="pbt-stay-card__actions">
          <span className={badge.className}>
            {coverage.isFull && <CheckCircle2 size={14} />} {badge.label}
          </span>
          <button
            type="button"
            className={`btn btn-sm ${panelOpen ? 'btn-outline-secondary' : 'pbt-btn-book'}`}
            onClick={() => setPanelOpen((v) => !v)}
          >
            {panelOpen ? 'Close' : 'Book Room'}
          </button>
        </div>
      </div>

      {assignedGroups.map((group) => (
        <RoomSegmentCard
          key={group.booking_ids[0]}
          group={group}
          stayInKey={stayInKey}
          stayOutKey={stayOutKey}
          competitionId={competitionId}
          participantId={participantId}
          guests={guests}
          onChanged={onChanged}
        />
      ))}

      {assignedGroups.length === 0 && !panelOpen && (
        <div className="pbt-stay-card__empty">
          <BedDouble size={18} />
          <span>No bookings yet</span>
        </div>
      )}

      {panelOpen && (
        <ParticipantBookingPanel
          mode="create"
          stayInKey={stayInKey}
          stayOutKey={stayOutKey}
          competitionId={competitionId}
          participantId={participantId}
          existingSegments={assignedGroups}
          guests={guests}
          onDone={handleDone}
        />
      )}
    </div>
  );
};

const ParticipantBookingTab = ({ participant, onNavigateToTab }) => {
  const arrivalPeriods = participant?.arrival_departure_periods || [];
  const stayPeriods = useMemo(() => participant?.stay_periods || [], [participant?.stay_periods]);
  const competitions = participant?.competition_id || [];
  const personId = participant?.person_id?._id;

  const [selectedCompetitionId, setSelectedCompetitionId] = useState('');
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupsError, setGroupsError] = useState('');
  const [guests, setGuests] = useState([]);

  useEffect(() => {
    const first = competitions[0];
    setSelectedCompetitionId(first?._id || first || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant?._id]);

  const fetchGroups = useCallback(async () => {
    if (!personId) return;
    setLoadingGroups(true);
    setGroupsError('');
    try {
      const res = await API.get('/ems/room-bookings/grouped', { params: { participant_id: personId } });
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setGroupsError(err.response?.data?.message || 'Failed to load bookings');
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }, [personId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const fetchGuests = useCallback(async () => {
    if (!participant?._id) return;
    try {
      const res = await API.get(`/ems/participants/${participant._id}/guests`);
      setGuests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setGuests([]);
    }
  }, [participant?._id]);

  useEffect(() => {
    fetchGuests();
  }, [fetchGuests]);

  const { periodGroups, orphanGroups } = useMemo(
    () => assignGroupsToPeriods(groups, stayPeriods),
    [groups, stayPeriods]
  );

  const grandTotal = useMemo(
    () => groups.reduce((sum, g) => sum + Number(g.price || 0) * (g.nights || 0), 0),
    [groups]
  );

  return (
    <div className="pbt-root">
      {competitions.length > 1 && (
        <div className="pbt-competition-select">
          <label className="pbt-label" htmlFor="pbt-competition">Booking for</label>
          <select
            id="pbt-competition"
            className="form-select form-select-sm pbt-select"
            value={selectedCompetitionId}
            onChange={(e) => setSelectedCompetitionId(e.target.value)}
          >
            {competitions.map((c) => {
              const id = c?._id || c;
              const name = c?.name || 'Competition';
              return <option key={id} value={id}>{name}</option>;
            })}
          </select>
        </div>
      )}

      <section className="pbt-section">
        <div className="pbt-section__header">
          <span className="pbt-section__icon"><PlaneTakeoff size={18} /></span>
          <h3 className="pbt-section__title">Arrival &amp; Departure Periods</h3>
        </div>
        {arrivalPeriods.length === 0 ? (
          <p className="pbt-empty">No arrival/departure periods defined.</p>
        ) : (
          <div className="pbt-period-list">
            {arrivalPeriods.map((p, i) => (
              <div key={i} className="pbt-period-row">
                <span className="pbt-period-row__label">Period {i + 1}</span>
                <span className="pbt-date-chip"><PlaneLanding size={12} /> {formatDate(p.arrival_date)}</span>
                <span className="pbt-arrow">→</span>
                <span className="pbt-date-chip"><PlaneTakeoff size={12} /> {formatDate(p.departure_date)}</span>
                <span className="pbt-nights-badge">{nightsBetween(p.arrival_date, p.departure_date)} days</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="pbt-section pbt-section--accommodation">
        <div className="pbt-section__header">
          <span className="pbt-section__icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
            </svg>
          </span>
          <h3 className="pbt-section__title">Accommodation Stay Periods</h3>
          {grandTotal > 0 && (
            <span className="pbt-section__total">${grandTotal.toLocaleString()}</span>
          )}
        </div>

        {groupsError && (
          <div className="pbt-panel__error-banner">
            <span>{groupsError}</span>
            <button type="button" className="btn btn-sm pbt-btn-retry" onClick={fetchGroups}>
              <RefreshCw size={12} /> Retry
            </button>
          </div>
        )}

        {loadingGroups && !groupsError && (
          <p className="pbt-empty">Loading bookings…</p>
        )}

        {stayPeriods.length === 0 ? (
          <div className="pbt-no-stays">
            <p className="mb-1 fw-medium">No stay periods defined for this participant.</p>
            <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
              Add check-in / check-out windows via the Registration tab before booking a room.
            </p>
            {onNavigateToTab && (
              <button type="button" className="btn btn-link p-0 pbt-empty-cta" onClick={() => onNavigateToTab('REGISTRATION')}>
                Go to Registration tab →
              </button>
            )}
          </div>
        ) : (
          <div className="pbt-stay-list">
            {stayPeriods.map((p, i) => (
              <StayPeriodCard
                key={i}
                period={p}
                index={i}
                assignedGroups={periodGroups.get(i) || []}
                competitionId={selectedCompetitionId}
                participantId={personId}
                guests={guests}
                onChanged={fetchGroups}
              />
            ))}
          </div>
        )}

        {orphanGroups.length > 0 && (
          <div className="pbt-orphan-section">
            <p className="pbt-orphan-section__label">
              Other bookings — not matching a defined stay period
            </p>
            {orphanGroups.map((group) => (
              <RoomSegmentCard
                key={group.booking_ids[0]}
                group={group}
                competitionId={selectedCompetitionId}
                participantId={personId}
                guests={guests}
                onChanged={fetchGroups}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ParticipantBookingTab;
